/**
 * Lobby store — multiplayer connection, identity, room list, current room, chat — plus the
 * single-player launcher. The UI codes against `LobbyState`; see docs/Networking.md.
 *
 * Identity: the server issues a reconnect token in `welcome`. It is kept in sessionStorage (per
 * browser tab — two tabs are two players, handy for hot-seat testing) and sent with every
 * `hello` — after a dropped connection (WsConnection reconnects automatically) or a page reload
 * the server restores the same player, room seat and match. The player name lives in localStorage.
 *
 * Screen routing contract (App.tsx): `room !== null` shows the lobby room; a game view in
 * gameStore shows the game. The store keeps both consistent: leaving, being removed, losing the
 * connection for good or the room disappearing all clear `room` and, for multiplayer games,
 * reset gameStore.
 */
import type { AiDifficulty, ForcedWeather, SessionConfig } from '@friction/engine';
import {
  type ChatLine,
  CloseCode,
  type GameSpeed,
  LIMITS,
  normalizeRoomCode,
  PROTOCOL_VERSION,
  type RoomInfo,
  type RoomSettings,
  type RoomSummary,
  type ServerMessage,
  type SlotController,
  sanitizeName,
  sanitizeText,
} from '@friction/protocol';
import { createStore, useStore } from 'zustand';
import { gameStore } from '../game/store';
import { type ConnectionStatus, getConnection, setConnection } from './connection';
import { LocalConnection } from './local';
import { routeGameMessage } from './router';
import { WsConnection } from './ws';

export interface LobbyState {
  status: 'offline' | 'connecting' | 'online';
  playerId: string | null;
  playerName: string;
  rooms: RoomSummary[];
  room: RoomInfo | null;
  chat: ChatLine[];
  /** Last error, as the server or client worded it (English); shown via net/serverText.ts. */
  error: string | null;
  /** Machine-readable reason of `error` when the server sent one. */
  errorCode: string | null;
  /** Round-trip latency in ms (multiplayer), or null. */
  ping: number | null;

  /** Connect (or reconnect under a new name). Never rejects; resolves once online or failed. */
  connect(name: string): Promise<void>;
  disconnect(): void;
  refreshRooms(): void;
  createRoom(settings: Partial<RoomSettings>): void;
  joinRoom(code: string): void;
  leaveRoom(): void;
  updateSettings(settings: Partial<RoomSettings>): void;
  setSlot(
    slot: number,
    patch: { controller?: SlotController; aiDifficulty?: AiDifficulty; team?: number; color?: number },
  ): void;
  takeSlot(slot: number): void;
  setReady(ready: boolean): void;
  startGame(): void;
  sendChat(text: string): void;
  clearError(): void;
  /** Leave the running game (single- or multiplayer) and return to the menu. */
  quitGame(): void;
}

// ---- persistence ------------------------------------------------------------------------------

const NAME_KEY = 'friction.playerName';
const TOKEN_KEY = 'friction.token';

/** Web Storage wrapper that never throws (private mode, disabled storage, non-browser tests). */
function safeStorage(pick: () => Storage | undefined) {
  return {
    get(key: string): string | null {
      try {
        return pick()?.getItem(key) ?? null;
      } catch {
        return null;
      }
    },
    set(key: string, value: string): void {
      try {
        pick()?.setItem(key, value);
      } catch {
        // not persisted — reconnect still works within this page's lifetime
      }
    },
  };
}
/** Long-lived preferences (player name). */
const storage = safeStorage(() => globalThis.localStorage);
/** Per-tab identity (reconnect token). */
const tabStorage = safeStorage(() => globalThis.sessionStorage);

// ---- connection plumbing ----------------------------------------------------------------------

const CHAT_LIMIT = 200;
const CONNECT_TIMEOUT_MS = 15_000;

/** The lobby's multiplayer connection (also the game connection while in a multiplayer match). */
let ws: WsConnection | null = null;
/** Token held in memory too, so reconnects work even where localStorage is unavailable. */
let token: string | null = tabStorage.get(TOKEN_KEY);
/** Pending `connect()` promise. */
let pending: { resolve: () => void; timer: ReturnType<typeof setTimeout> } | null = null;
/** Set on `welcome`; cleared by the first `room` message. See the `roomList` handler. */
let awaitingRoom = false;

const FATAL_REASONS: Record<number, string> = {
  [CloseCode.version]: 'This client is out of date with the server. Reload the page.',
  [CloseCode.replaced]: 'You connected from another window or tab.',
  [CloseCode.rateLimited]: 'Disconnected for sending too many messages.',
  [CloseCode.tooBig]: 'Disconnected: a message was too large.',
  [CloseCode.policy]: 'Disconnected by the server.',
};

function resolvePending(): void {
  if (!pending) return;
  clearTimeout(pending.timer);
  pending.resolve();
  pending = null;
}

function mpGameActive(): boolean {
  return gameStore.getState().view !== null && getConnection()?.kind === 'ws';
}

/** Leave any multiplayer game view (room gone, connection lost …). */
function endMpGame(): void {
  if (mpGameActive()) gameStore.getState().reset();
}

function sendWs(msg: Parameters<WsConnection['send']>[0]): void {
  ws?.send(msg);
}

function onWsStatus(conn: WsConnection, status: ConnectionStatus): void {
  if (conn !== ws) return;
  if (status === 'open') {
    conn.send({
      t: 'hello',
      version: PROTOCOL_VERSION,
      name: lobbyStore.getState().playerName,
      token: token ?? undefined,
    });
    return;
  }
  if (status === 'connecting') {
    lobbyStore.setState({ status: 'connecting', ping: null });
    return;
  }
  // closed
  const info = conn.closeInfo;
  endMpGame();
  if (getConnection() === conn) setConnection(null);
  ws = null;
  lobbyStore.setState({
    status: 'offline',
    room: null,
    chat: [],
    ping: null,
    errorCode: null,
    error: info
      ? (FATAL_REASONS[info.code] ??
        `Lost connection to the server${info.reason ? ` (${info.reason})` : ''}.`)
      : null,
  });
  resolvePending();
}

function onWsMessage(conn: WsConnection, msg: ServerMessage): void {
  if (conn !== ws) return;
  if (routeGameMessage(msg)) return;
  switch (msg.t) {
    case 'welcome':
      token = msg.token;
      tabStorage.set(TOKEN_KEY, msg.token);
      awaitingRoom = true;
      // The server replays room chat history after welcome if we are (still) seated somewhere.
      lobbyStore.setState({
        status: 'online',
        playerId: msg.playerId,
        playerName: msg.name,
        chat: [],
        error: null,
      });
      resolvePending();
      conn.send({ t: 'listRooms' });
      return;
    case 'roomList':
      if (awaitingRoom) {
        // No `room` arrived between welcome and this reply: we hold no seat anywhere (e.g. the
        // room expired while we were away). Drop any stale room/game view.
        awaitingRoom = false;
        if (lobbyStore.getState().room) {
          endMpGame();
          lobbyStore.setState({ room: null, chat: [] });
        }
      }
      lobbyStore.setState({ rooms: msg.rooms });
      return;
    case 'room': {
      awaitingRoom = false;
      const prev = lobbyStore.getState().room;
      const next = msg.room;
      const sameRoom = prev !== null && next !== null && prev.code === next.code;
      lobbyStore.setState((s) => ({ room: next, chat: sameRoom ? s.chat : [] }));
      if (!next) {
        endMpGame();
        conn.send({ t: 'listRooms' });
      }
      return;
    }
    case 'chat':
      lobbyStore.setState((s) => ({ chat: [...s.chat, msg.line].slice(-CHAT_LIMIT) }));
      return;
    case 'error':
      lobbyStore.setState({ error: msg.message, errorCode: msg.code ?? null });
      return;
    case 'pong':
      lobbyStore.setState({ ping: Math.max(0, Math.round(performance.now() - msg.time)) });
      return;
  }
}

function closeWs(): void {
  const conn = ws;
  ws = null;
  if (!conn) return;
  if (getConnection() === conn) setConnection(null);
  else conn.close();
}

// ---- store ------------------------------------------------------------------------------------

export const lobbyStore = createStore<LobbyState>()((set, get) => ({
  status: 'offline',
  playerId: null,
  playerName: storage.get(NAME_KEY) ?? '',
  rooms: [],
  room: null,
  chat: [],
  error: null,
  errorCode: null,
  ping: null,

  connect(name) {
    const clean = sanitizeName(name) || get().playerName;
    if (clean) storage.set(NAME_KEY, clean);
    if (ws && get().status === 'online' && clean === get().playerName) return Promise.resolve();
    // (Re)connect. A rename reconnects too: the token keeps identity, seat and match.
    resolvePending();
    closeWs();
    // Multiplayer replaces any single-player match (one connection at a time).
    if (getConnection()?.kind === 'local') {
      setConnection(null);
      gameStore.getState().reset();
    }
    set({ status: 'connecting', playerName: clean, error: null, ping: null });
    const conn = new WsConnection();
    ws = conn;
    conn.onStatus((st) => onWsStatus(conn, st));
    conn.onMessage((msg) => onWsMessage(conn, msg));
    setConnection(conn);
    if (conn.status === 'closed') onWsStatus(conn, 'closed'); // invalid URL: failed synchronously
    if (ws !== conn) return Promise.resolve();
    return new Promise<void>((resolve) => {
      pending = { resolve, timer: setTimeout(resolvePending, CONNECT_TIMEOUT_MS) };
    });
  },

  disconnect() {
    endMpGame();
    closeWs();
    resolvePending();
    set({ status: 'offline', playerId: null, rooms: [], room: null, chat: [], ping: null });
  },

  refreshRooms() {
    sendWs({ t: 'listRooms' });
  },

  createRoom(settings) {
    sendWs({ t: 'createRoom', settings });
  },

  joinRoom(code) {
    sendWs({ t: 'joinRoom', code: normalizeRoomCode(code) });
  },

  leaveRoom() {
    sendWs({ t: 'leaveRoom' });
    set({ room: null, chat: [] });
  },

  updateSettings(settings) {
    sendWs({ t: 'updateSettings', settings });
  },

  setSlot(slot, patch) {
    sendWs({ t: 'setSlot', slot, ...patch });
  },

  takeSlot(slot) {
    sendWs({ t: 'takeSlot', slot });
  },

  setReady(ready) {
    sendWs({ t: 'ready', ready });
  },

  startGame() {
    sendWs({ t: 'startGame' });
  },

  sendChat(text) {
    const clean = sanitizeText(text, LIMITS.chatText);
    if (clean) sendWs({ t: 'chat', text: clean });
  },

  clearError() {
    set({ error: null, errorCode: null });
  },

  quitGame() {
    const conn = getConnection();
    if (conn?.kind === 'local') setConnection(null);
    else if (conn?.kind === 'ws' && get().room) {
      // Leaving a running match hands our faction to the AI; the connection stays up for the lobby.
      conn.send({ t: 'leaveRoom' });
      conn.send({ t: 'listRooms' });
      set({ room: null, chat: [] });
    }
    gameStore.getState().reset();
  },
}));

export function useLobby<T>(selector: (s: LobbyState) => T): T {
  return useStore(lobbyStore, selector);
}

/**
 * Start a single-player match in a Web Worker. Replaces (closes) any current connection —
 * including a multiplayer one — and routes the worker's messages into gameStore.
 */
export function startSinglePlayer(
  config: SessionConfig,
  speed: GameSpeed = 3,
  /** `devWeather` pins the sky (dev builds' weather checks; the match is then not replayable). */
  opts: { paused?: boolean; devWeather?: ForcedWeather } = {},
): void {
  const conn = new LocalConnection(config, speed, undefined, opts.paused ?? false, opts.devWeather);
  conn.onMessage((msg) => {
    if (routeGameMessage(msg)) return;
    if (msg.t === 'error') lobbyStore.setState({ error: msg.message, errorCode: msg.code ?? null });
  });
  setConnection(conn);
}
